import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Upload, Search, FolderOpen, Folder, ChevronRight, Home, ArrowLeft, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const MediaPickerModal = ({ open, onClose, onSelect }) => {
  const [media, setMedia] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  // null = root level. Otherwise the folder_id we're currently browsing.
  const [currentFolderId, setCurrentFolderId] = useState(null);

  useEffect(() => {
    if (open) {
      fetchMedia();
      // Each time the modal opens, reset to root + clear search so the user
      // sees the folder structure first, not whatever was browsed last.
      setCurrentFolderId(null);
      setSearchTerm('');
    }
  }, [open]);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const [mediaRes, foldersRes] = await Promise.all([
        api.get('/media'),
        api.get('/media/folders').catch(() => ({ data: [] })),
      ]);
      setMedia(mediaRes.data || []);
      setFolders(foldersRes.data || []);
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  // Lookup: folder_id -> full path string (e.g. "Marketing / Hero Images")
  const folderPathById = useMemo(() => {
    const byId = new Map(folders.map((f) => [f.folder_id, f]));
    const cache = new Map();
    const resolve = (folderId) => {
      if (!folderId) return 'Root';
      if (cache.has(folderId)) return cache.get(folderId);
      const parts = [];
      let current = byId.get(folderId);
      const seen = new Set();
      while (current && !seen.has(current.folder_id)) {
        seen.add(current.folder_id);
        parts.unshift(current.name || 'Unnamed');
        current = current.parent_id ? byId.get(current.parent_id) : null;
      }
      const path = parts.length ? parts.join(' / ') : 'Root';
      cache.set(folderId, path);
      return path;
    };
    return resolve;
  }, [folders]);

  // Breadcrumb trail (Root > ... > current folder)
  const breadcrumbs = useMemo(() => {
    if (!currentFolderId) return [];
    const byId = new Map(folders.map((f) => [f.folder_id, f]));
    const trail = [];
    let current = byId.get(currentFolderId);
    const seen = new Set();
    while (current && !seen.has(current.folder_id)) {
      seen.add(current.folder_id);
      trail.unshift(current);
      current = current.parent_id ? byId.get(current.parent_id) : null;
    }
    return trail;
  }, [currentFolderId, folders]);

  // Child folders directly under the current folder (or root)
  const childFolders = useMemo(() => {
    return folders.filter((f) =>
      currentFolderId ? f.parent_id === currentFolderId : !f.parent_id
    );
  }, [folders, currentFolderId]);

  // While searching, ignore the folder filter and search ALL media.
  // Without a search term, only show files in the current folder.
  // Matches against the same 6 fields as the main Media Library:
  //   filename, original_filename, alt_text, title, caption, custom_slug
  // — so a tenant can find e.g. a hero image by its alt text or caption.
  const isSearching = searchTerm.trim().length > 0;
  const filteredMedia = useMemo(() => {
    if (isSearching) {
      const q = searchTerm.toLowerCase();
      return media.filter((item) => {
        const haystacks = [
          item.filename,
          item.original_filename,
          item.alt_text,
          item.title,
          item.caption,
          item.custom_slug,
        ];
        return haystacks.some((s) => typeof s === 'string' && s.toLowerCase().includes(q));
      });
    }
    return media.filter((item) =>
      currentFolderId ? item.folder_id === currentFolderId : true
    );
  }, [media, currentFolderId, searchTerm, isSearching]);

  // Pagination — same approach as the full Media Library page.
  const PAGE_SIZE = 60;
  const [currentPage, setCurrentPage] = useState(1);

  // Sort order — shared key with the main Media Library page so editors get
  // their preferred order in both surfaces.
  const [sortOrder, setSortOrder] = useState(() => {
    try { return localStorage.getItem('nexuscms_media_sort') || 'newest'; }
    catch (e) { return 'newest'; }
  });
  useEffect(() => {
    try { localStorage.setItem('nexuscms_media_sort', sortOrder); }
    catch (e) { /* ignore */ }
  }, [sortOrder]);

  useEffect(() => {
    setCurrentPage(1);
  }, [currentFolderId, searchTerm, sortOrder]);

  const sortedMedia = useMemo(() => {
    const arr = [...filteredMedia];
    const tsOf = (m) => new Date(m.uploaded_at || 0).getTime();
    const nameOf = (m) => (m.filename || '').toLowerCase();
    const sizeOf = (m) => Number(m.size || 0);
    switch (sortOrder) {
      case 'oldest': arr.sort((a, b) => tsOf(a) - tsOf(b)); break;
      case 'name-asc': arr.sort((a, b) => nameOf(a).localeCompare(nameOf(b))); break;
      case 'name-desc': arr.sort((a, b) => nameOf(b).localeCompare(nameOf(a))); break;
      case 'size-desc': arr.sort((a, b) => sizeOf(b) - sizeOf(a)); break;
      case 'size-asc': arr.sort((a, b) => sizeOf(a) - sizeOf(b)); break;
      case 'newest':
      default: arr.sort((a, b) => tsOf(b) - tsOf(a)); break;
    }
    return arr;
  }, [filteredMedia, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(sortedMedia.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pagedMedia = sortedMedia.slice(pageStart, pageStart + PAGE_SIZE);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    if (currentFolderId) formData.append('folder_id', currentFolderId);

    try {
      await api.post('/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('File uploaded');
      fetchMedia();
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      // Clear the input so re-uploading the same file works
      e.target.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh]" data-testid="media-picker-modal">
        <DialogHeader>
          <DialogTitle>Select from Media Library</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search + Upload */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Search media — filename, alt text, title, caption…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-9"
                data-testid="media-search"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                  title="Clear search"
                  data-testid="media-search-clear"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger
                className="h-10 w-full sm:w-[170px] shrink-0"
                data-testid="media-picker-sort-trigger"
              >
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest first</SelectItem>
                <SelectItem value="oldest">Oldest first</SelectItem>
                <SelectItem value="name-asc">Name A – Z</SelectItem>
                <SelectItem value="name-desc">Name Z – A</SelectItem>
                <SelectItem value="size-desc">Largest first</SelectItem>
                <SelectItem value="size-asc">Smallest first</SelectItem>
              </SelectContent>
            </Select>
            <div>
              <input
                type="file"
                id="media-upload-modal"
                className="hidden"
                onChange={handleUpload}
                accept="image/*"
                disabled={uploading}
              />
              <label htmlFor="media-upload-modal">
                <Button asChild disabled={uploading} data-testid="upload-media-button">
                  <span className="cursor-pointer">
                    <Upload size={18} className="mr-2" />
                    {uploading ? 'Uploading...' : 'Upload here'}
                  </span>
                </Button>
              </label>
            </div>
          </div>

          {isSearching && (
            <p className="text-xs text-slate-500" data-testid="media-picker-search-result-count">
              {filteredMedia.length} {filteredMedia.length === 1 ? 'result' : 'results'} for
              <span className="font-medium text-slate-700"> "{searchTerm}"</span> across all folders
            </p>
          )}

          {/* Breadcrumbs — clickable trail to jump back up the folder tree.
              Hidden while searching since search ignores the folder filter. */}
          {!isSearching && (
            <div className="flex items-center gap-1 text-sm text-slate-600 flex-wrap" data-testid="media-picker-breadcrumbs">
              <button
                type="button"
                onClick={() => setCurrentFolderId(null)}
                className={`flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100 ${!currentFolderId ? 'text-indigo-600 font-semibold' : ''}`}
                data-testid="media-picker-breadcrumb-root"
              >
                <Home size={14} />
                <span>Root</span>
              </button>
              {breadcrumbs.map((f, idx) => (
                <React.Fragment key={f.folder_id}>
                  <ChevronRight size={14} className="text-slate-400" />
                  <button
                    type="button"
                    onClick={() => setCurrentFolderId(f.folder_id)}
                    className={`px-2 py-1 rounded hover:bg-slate-100 ${idx === breadcrumbs.length - 1 ? 'text-indigo-600 font-semibold' : ''}`}
                    data-testid={`media-picker-breadcrumb-${f.folder_id}`}
                  >
                    {f.name || 'Unnamed'}
                  </button>
                </React.Fragment>
              ))}
              {currentFolderId && (
                <button
                  type="button"
                  onClick={() => {
                    const parent = folders.find((f) => f.folder_id === currentFolderId)?.parent_id || null;
                    setCurrentFolderId(parent);
                  }}
                  className="ml-auto flex items-center gap-1 text-xs text-slate-500 hover:text-indigo-600"
                  data-testid="media-picker-up-one-level"
                >
                  <ArrowLeft size={12} />
                  Up one level
                </button>
              )}
            </div>
          )}

          {/* Body */}
          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading media...</div>
          ) : (
            <div className="max-h-[420px] overflow-y-auto p-1 space-y-4">
              {/* Folders (only when not searching) */}
              {!isSearching && childFolders.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 px-1">
                    Folders
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {childFolders.map((folder) => {
                      const itemCount = media.filter((m) => m.folder_id === folder.folder_id).length;
                      return (
                        <button
                          key={folder.folder_id}
                          type="button"
                          onClick={() => setCurrentFolderId(folder.folder_id)}
                          className="flex items-center gap-2 p-3 bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-300 rounded-lg text-left transition-all"
                          data-testid={`media-picker-folder-${folder.folder_id}`}
                          title={folder.name}
                        >
                          <Folder size={20} className="text-indigo-600 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-slate-800 truncate">
                              {folder.name || 'Unnamed'}
                            </div>
                            <div className="text-[10px] text-slate-500">
                              {itemCount} {itemCount === 1 ? 'file' : 'files'}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Files */}
              <div>
                {!isSearching && childFolders.length > 0 && filteredMedia.length > 0 && (
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 px-1">
                    {currentFolderId ? 'Files in this folder' : 'All files — most recent first'}
                  </div>
                )}

                {filteredMedia.length === 0 ? (
                  <div className="text-center py-10 text-slate-500 text-sm">
                    {isSearching ? 'No media match your search.' : (currentFolderId ? (childFolders.length > 0 ? 'No files in this folder yet.' : 'This folder is empty.') : 'No media uploaded yet.')}
                  </div>
                ) : (
                  <>
                  <div className="grid grid-cols-4 gap-4">
                    {pagedMedia.map((item) => {
                      const folderPath = folderPathById(item.folder_id);
                      return (
                        <button
                          key={item.media_id}
                          onClick={() => {
                            onSelect(item);
                            onClose();
                          }}
                          data-testid={`media-item-select-${item.media_id}`}
                          className="group relative flex flex-col bg-white rounded-lg overflow-hidden hover:ring-2 hover:ring-indigo-500 transition-all border border-slate-200 text-left"
                          title={`${item.filename} — ${folderPath}`}
                        >
                          <div className="relative aspect-square bg-slate-100">
                            {item.mime_type?.startsWith('image/') ? (
                              <img
                                src={item.url}
                                alt={item.filename}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-4xl text-slate-300">
                                📄
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                              <span className="text-white opacity-0 group-hover:opacity-100 font-medium">Select</span>
                            </div>
                          </div>
                          <div className="px-2 py-1.5 border-t border-slate-100">
                            <div className="text-xs font-medium text-slate-700 truncate" title={item.filename}>
                              {item.filename}
                            </div>
                            {/* Always show the folder path in root + search views so
                                users can see at-a-glance where a file lives. In a
                                specific folder view it's redundant. */}
                            {(isSearching || !currentFolderId) && (
                              <div className="flex items-center gap-1 text-[10px] text-slate-500 truncate mt-0.5" data-testid={`media-item-folder-${item.media_id}`}>
                                <FolderOpen size={10} className="shrink-0" />
                                <span className="truncate">{folderPath}</span>
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {totalPages > 1 && (
                    <div
                      className="flex items-center justify-between mt-4 px-1"
                      data-testid="media-picker-pagination"
                    >
                      <p className="text-xs text-slate-500">
                        {pageStart + 1}
                        {'–'}
                        {Math.min(pageStart + PAGE_SIZE, filteredMedia.length)}
                        {' '}of {filteredMedia.length}
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={safePage <= 1}
                          data-testid="media-picker-pagination-prev"
                        >
                          Previous
                        </Button>
                        <span className="text-xs text-slate-600 px-1">
                          Page <span className="font-medium">{safePage}</span> of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          disabled={safePage >= totalPages}
                          data-testid="media-picker-pagination-next"
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MediaPickerModal;
